import { BinarySearchTree } from './binary-search-tree';

interface PriorityQueueEntry {
  id: string;
  priority: number;
  sequence: number;
}

export class PriorityQueue {
  tree = new BinarySearchTree<PriorityQueueEntry>((a, b) => {
    if (a.id === b.id) return 0;
    if (a.priority !== b.priority) return a.priority - b.priority;

    // `pop()` takes the max node. For equal priority, the oldest queued entry
    // must win so causally ordered doc writes keep their scheduling order.
    return b.sequence - a.sequence;
  });
  priorityMap = new Map<string, PriorityQueueEntry>();
  private sequence = 0;

  push(id: string, priority: number = 0) {
    const oldEntry = this.priorityMap.get(id);
    if (oldEntry?.priority === priority) {
      return;
    }
    if (oldEntry) {
      this.remove(id);
    }
    const entry = {
      id,
      priority,
      sequence: oldEntry?.sequence ?? this.sequence++,
    };
    this.tree.insert(entry);
    this.priorityMap.set(id, entry);
  }

  pop(minimumPriority?: number) {
    const node = this.tree.max();

    if (!node) {
      return null;
    }

    if (
      minimumPriority !== undefined &&
      node.getValue().priority < minimumPriority
    ) {
      return null;
    }

    this.tree.removeNode(node);

    const { id } = node.getValue();
    this.priorityMap.delete(id);

    return id;
  }

  remove(id: string) {
    const entry = this.priorityMap.get(id);
    if (entry === undefined) {
      return false;
    }
    const removed = this.tree.remove(entry);
    if (removed) {
      this.priorityMap.delete(id);
    }

    return removed;
  }

  has(id: string) {
    return this.priorityMap.has(id);
  }

  clear() {
    this.tree.clear();
    this.priorityMap.clear();
    this.sequence = 0;
  }

  setPriority(id: string, priority: number) {
    if (this.remove(id)) {
      this.push(id, priority);
    }
  }

  get length() {
    return this.tree.count.bind(this.tree);
  }
}
